import req from '@sportninja/common/api/request'
import { useCallback, useState } from 'react'

/**
 * Interface representing the subscription type with its features
 */
interface SubscriptionTypeFeature {
  id: number
  name: string
  rule: string | null
  has_feature: boolean
  created_at: string
  updated_at: string
}

/**
 * Interface representing the subscription type
 */
interface SubscriptionType {
  id: number
  name: string
  price_monthly: number
  price_yearly: number
  features: SubscriptionTypeFeature[]
  created_at: string
  updated_at: string
}

/**
 * Interface representing the payment provider type
 */
interface PaymentProviderType {
  id: string
  name: string
}

/**
 * Interface representing the organization
 */
interface Organization {
  name: string
  url: string
  image: string
}

/**
 * Interface representing the country
 */
interface Country {
  id: number
  name: string
  name_full: string
  iso_3166_2: string
  iso_3166_3: string
  capital: string
  citizenship: string
  currency_symbol: string
  is_visible: boolean
}

/**
 * Interface representing the province
 */
interface Province {
  id: number
  country_id: number
  name: string
  iso_3166_2: string
  geonames_id: number
  geonames_code: string
}

/**
 * Interface representing the address
 */
interface Address {
  id: string
  name: string
  name_full: string | null
  street_1: string
  street_2: string | null
  city: string
  postal_code: string
  latitude: string | null
  longitude: string | null
  updated_at: string
  country: Country
  province: Province
  /** IANA timezone for the account address, e.g. 'America/New_York' */
  timezone?: string
}

/**
 * Interface representing the account data
 */
export interface AccountData {
  uid: string
  full_name: string
  title: string
  company_name: string
  email: string
  phone_number: string
  organization?: Organization
  address: Address
  subscription_type: SubscriptionType
  subscription: any | null
  subscription_usage: any | null
  subscription_upcoming_invoice: any | null
  payment_method: any | null
  participants: number
  payment_provider_type: PaymentProviderType | null
  /**
   * Merchant-account processing rates, seeded when an account connects and
   * editable in Nova only. Both read 0 when no merchant account is connected.
   */
  payment_provider_application_fee?: number | null
  payment_provider_percentage_fee?: number | null
  taxes: any[]
  integrations?: {
    daysmart?: { is_connected: boolean }
    usahockey?: { is_enabled: boolean | null }
    hockeycanada?: { is_enabled: boolean | null }
  }
}

/**
 * Hook to fetch and manage account data
 * @param accountId - The ID of the account to fetch
 * @returns Object containing account data, loading state, error state, and refetch function
 */
export const useAccount = (accountId?: string) => {
  const [data, setData] = useState<AccountData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchData = useCallback(async () => {
    if (!accountId) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await req(`/account/${accountId}`)
      setData(response.data)
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('An error occurred while fetching account data')
      )
    } finally {
      setLoading(false)
    }
  }, [accountId])

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  }
}
